import type {
  PaywallPlanCardDefinition,
  PaywallPlanDensity,
} from "@/components/modal/paywall/paywall-modal-components";
import { PROACTIVE_PLAN_FEATURES } from "@/components/modal/paywall/plan-features";

export const PROACTIVE_MONEY_BACK_GUARANTEE_NOTE =
  "30-day money-back guarantee—just ask. Cancel anytime.";

export const getProactiveMonthlyPriceCopy = (proactivePrice?: string) =>
  proactivePrice?.replace(/\/mo\b/, "/month");

export interface ProactivePlanActionCopy {
  buttonLabel: string;
  note?: string;
  pricingUnavailable: boolean;
}

export const getProactivePlanActionCopy = ({
  isTrialEligible,
  proactivePrice,
  pricingError,
  pricingIsLoading,
}: {
  isTrialEligible: boolean;
  proactivePrice?: string;
  pricingError?: string;
  pricingIsLoading: boolean;
}): ProactivePlanActionCopy => {
  const pricingUnavailable = Boolean(pricingError) || (!proactivePrice && !pricingIsLoading);

  if (pricingUnavailable) {
    return {
      buttonLabel: "Retry pricing",
      pricingUnavailable,
    };
  }

  const monthlyPriceCopy = getProactiveMonthlyPriceCopy(proactivePrice);
  if (isTrialEligible) {
    return {
      buttonLabel: "Start 7-day free trial",
      note: monthlyPriceCopy
        ? `$0 today. Then ${monthlyPriceCopy} after 7 days unless canceled.`
        : undefined,
      pricingUnavailable,
    };
  }

  return {
    buttonLabel: "Upgrade to Proactive",
    note: monthlyPriceCopy ? `${monthlyPriceCopy}. Cancel anytime.` : undefined,
    pricingUnavailable,
  };
};

const getProactivePlanPrice = ({
  isTrialEligible,
  proactivePrice,
  pricingError,
}: {
  isTrialEligible: boolean;
  proactivePrice?: string;
  pricingError?: string;
}): string => {
  if (pricingError) {
    return "Connect to view";
  }

  if (isTrialEligible) {
    return "Free 7-day trial";
  }

  return proactivePrice ?? "Loading";
};

export const buildProactivePlanCard = ({
  badgeLabel,
  density,
  isTrialEligible,
  proactivePrice,
  pricingError,
}: {
  badgeLabel?: string;
  density?: PaywallPlanDensity;
  isTrialEligible: boolean;
  proactivePrice?: string;
  pricingError?: string;
}): PaywallPlanCardDefinition => {
  const monthlyPriceCopy = getProactiveMonthlyPriceCopy(proactivePrice);

  return {
    id: "proactive",
    title: "Proactive",
    badgeLabel,
    price: getProactivePlanPrice({ isTrialEligible, proactivePrice, pricingError }),
    priceSubtitle:
      pricingError || !isTrialEligible || !monthlyPriceCopy
        ? undefined
        : `Then ${monthlyPriceCopy}`,
    features: PROACTIVE_PLAN_FEATURES,
    tone: "emphasis",
    featureLayout: "paragraph",
    footerNote: pricingError ? "Reconnect to view pricing and upgrade." : undefined,
    density,
  };
};
